import { reactive, readonly } from 'vue';
import { CHAIN_METADATA, getChainLabel, isSupportedChain } from '@/utils/chains';

// Backwards compatibility export (if other code imports SUPPORTED_CHAINS)
export const SUPPORTED_CHAINS = CHAIN_METADATA;

const state = reactive({
  walletChainId: null,       // raw chain id from provider
  effectiveChainId: null,    // null if unsupported
  supported: false,
  desiredChainId: null,
  switching: false,
  lastChangeTs: 0,
  correlationId: null,
  uiSelectedChainId: null,   // user-chosen chain filter for single-network NFT view
});

function classify(chainId) { return CHAIN_METADATA[chainId] || null; }

function updateWalletChain(chainId) {
  state.walletChainId = chainId;
  const meta = classify(chainId);
  state.supported = !!meta;
  state.effectiveChainId = meta ? chainId : null;
  state.lastChangeTs = Date.now();
  state.correlationId = `net-${state.lastChangeTs}-${chainId}`;
  if (!state.uiSelectedChainId && state.supported) {
    // Initialize UI selection to the first supported wallet chain if unset
    state.uiSelectedChainId = chainId;
  }
  if (state.desiredChainId === chainId) {
    state.switching = false;
    state.desiredChainId = null;
  }
  console.info('[network] wallet->app', {
    chainId,
    supported: state.supported,
    correlationId: state.correlationId,
  });
}

async function requestDesiredChain(chainId) {
  if (!window.ethereum) throw new Error('No injected provider');
  // Always regenerate a fresh correlationId for an explicit user/system switch intent
  const ts = Date.now();
  state.correlationId = `switch-${ts}-${chainId}`;
  if (state.walletChainId === chainId) {
    console.info('[network] requestDesiredChain noop (already on chain)', { chainId, correlationId: state.correlationId });
    state.switching = false;
    state.desiredChainId = null;
    return;
  }
  if (state.switching) {
    // Allow re-entrancy only to refresh correlation + desiredChainId (avoid overlapping provider calls)
    state.desiredChainId = chainId;
    console.info('[network] requestDesiredChain while switching (refresh correlation)', { chainId, correlationId: state.correlationId });
    return;
  }
  state.desiredChainId = chainId;
  state.switching = true;
  console.info('[network] switch requested', { desired: chainId, correlationId: state.correlationId });
  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId }],
    });
  } catch (err) {
    if (err.code === 4902) {
      const params = chainId === '0x2105'
        ? {
            chainId,
            chainName: 'Base Mainnet',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://mainnet.base.org'],
            blockExplorerUrls: ['https://basescan.org'],
          }
        : chainId === '0x14a34'
        ? {
            chainId,
            chainName: 'Base Sepolia',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://sepolia.base.org'],
            blockExplorerUrls: ['https://sepolia.basescan.org'],
          }
        : null;
      if (!params) {
        console.warn('[network] automatic chain addition unavailable', {
          chainId,
        });
        return;
      }
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [params],
      });
    } else {
      console.warn('[network] switch failed', err);
    }
  } finally {
    state.switching = false;
    state.desiredChainId = null;
  }
}

export function useNetworkStore() {
  return { state: readonly(state), updateWalletChain, requestDesiredChain, SUPPORTED_CHAINS, getChainLabel, isSupportedChain };
}

// External setter for UI chain (does not force wallet switch unless same-network sync desired)
export function setUiSelectedChain(chainId, { syncWallet = false } = {}) {
  if (!CHAIN_METADATA[chainId]) return;
  state.uiSelectedChainId = chainId;
  state.correlationId = `ui-${Date.now()}-${chainId}`;
  console.info('[network] uiSelectedChainId set', { chainId, correlationId: state.correlationId, syncWallet });
  if (syncWallet && state.walletChainId !== chainId) {
    requestDesiredChain(chainId);
  }
}
